/**
 * Usage settings page — plan-aware overview for the workspace owner.
 * Reads limits + live usage from /api/editor/me (Phase 1 billing). Storage also
 * shows the peak-last-30d billing metric. -1 limit = unlimited.
 */
import { Link } from "react-router-dom";
import {
  HardDrive,
  Users as UsersIcon,
  FolderKanban,
  Link2 as LinkIcon,
  ArrowUpRight,
} from "lucide-react";
import type { ReactNode } from "react";
import { SettingsPageHeader } from "@/pages/settings/settings-placeholder";
import { Button } from "@/components/ui/button";
import { useAuth, useMe } from "@/hooks/use-auth";
import { useStoragePeak } from "@/hooks/use-storage";
import { canViewUsage } from "@/lib/api-types";

function formatBytes(n: number | undefined | null): string {
  if (typeof n !== "number" || !Number.isFinite(n) || n < 0) return "—";
  if (n === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v < 10 ? v.toFixed(2) : v < 100 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}
const isUnlimited = (n: number | undefined | null) => typeof n === "number" && n < 0;

export function UsageSettingsPage() {
  const { role } = useAuth();
  const canSee = canViewUsage(role);
  const { data: me } = useMe();
  const peak = useStoragePeak(canSee);

  const limits = me?.limits ?? null;
  const usage = me?.usage ?? null;
  const planLabel = limits?.label ?? "—";
  const loading = !me;

  return (
    <div>
      <SettingsPageHeader
        title="Usage"
        subtitle={`Your ${planLabel} plan usage at a glance.`}
      />

      <div className="mb-6 flex items-center justify-between gap-4 rounded-2xl border border-border bg-secondary/40 p-5">
        <div>
          <div className="text-xs text-muted-foreground">Current plan</div>
          <div className="text-lg font-semibold text-foreground">{planLabel}</div>
        </div>
        <Button asChild size="sm">
          <Link to="/admin/settings/plan">
            Manage plan <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>

      <Meter
        icon={<HardDrive className="h-5 w-5" strokeWidth={1.75} />}
        label="Storage"
        used={usage?.storageBytes}
        limit={limits?.storageBytes}
        fmt={formatBytes}
        loading={loading}
        big
        sub={
          typeof peak.data?.peakLast30dBytes === "number"
            ? `Peak last 30 days — billed on this: ${formatBytes(peak.data.peakLast30dBytes)}`
            : "Active files + trash count"
        }
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Meter
          icon={<UsersIcon className="h-5 w-5" strokeWidth={1.75} />}
          label="Members"
          used={usage?.members}
          limit={limits?.members}
          loading={loading}
        />
        <Meter
          icon={<FolderKanban className="h-5 w-5" strokeWidth={1.75} />}
          label="Projects"
          used={usage?.projects}
          limit={limits?.projects}
          loading={loading}
        />
        <Meter
          icon={<LinkIcon className="h-5 w-5" strokeWidth={1.75} />}
          label="Review links"
          used={usage?.links}
          limit={limits?.links}
          loading={loading}
        />
      </div>
    </div>
  );
}

function Meter({
  icon,
  label,
  used,
  limit,
  fmt = (n: number) => `${n}`,
  loading,
  big,
  sub,
}: {
  icon: ReactNode;
  label: string;
  used: number | undefined | null;
  limit: number | undefined | null;
  fmt?: (n: number) => string;
  loading?: boolean;
  big?: boolean;
  sub?: string;
}) {
  const u = typeof used === "number" ? used : 0;
  const unlimited = isUnlimited(limit);
  const cap = typeof limit === "number" ? limit : 0;
  const fraction = unlimited || cap <= 0 ? 0 : Math.min(1, u / cap);
  const atLimit = !unlimited && cap > 0 && u >= cap;

  return (
    <div className="rounded-2xl border border-border bg-secondary/40 p-6">
      <div className="mb-4 grid h-9 w-9 place-items-center rounded-lg bg-secondary text-muted-foreground">
        {icon}
      </div>
      <div className="text-sm font-medium text-foreground">{label}</div>
      {loading ? (
        <div className="mt-4 h-6 w-32 animate-pulse rounded bg-secondary" />
      ) : (
        <>
          <div className="mt-2 flex items-baseline gap-1">
            <span className={(big ? "text-2xl" : "text-xl") + " font-semibold tabular-nums text-foreground"}>
              {fmt(u)}
            </span>
            <span className="text-sm text-muted-foreground">
              {unlimited ? " · Unlimited" : ` of ${fmt(cap)}`}
            </span>
          </div>
          {!unlimited && (
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-secondary">
              <div
                className={"h-full rounded-full transition-all " + (atLimit ? "bg-destructive" : "bg-primary")}
                style={{ width: `${Math.max(2, fraction * 100)}%` }}
              />
            </div>
          )}
          {sub && <div className="mt-2 text-xs text-muted-foreground">{sub}</div>}
          {atLimit && (
            <div className="mt-2 text-xs text-destructive">
              You've hit your plan limit. <Link to="/admin/settings/plan" className="underline">Upgrade</Link> for more.
            </div>
          )}
        </>
      )}
    </div>
  );
}
